import { Button, Dialog, IconButton } from '@mui/material';
import { FilterKeys } from '@schooly/api';
import {
  AgeGroupSelectMultiple,
  FilterRow,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { CrossIcon, ModalContent, ModalFooter, ModalHeader } from '@schooly/style';
import { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../../hooks/useSchool';
import { TimetableGridFilters } from '../utils';

type TimetableGridFiltersPopupProps = {
  filters: TimetableGridFilters;
  availableAgeGroupIds: string[];
  onClose: () => void;
  onSetFilters: (v: TimetableGridFilters) => void;
};

const emptyFilters: TimetableGridFilters = {
  [FilterKeys.AgeGroup]: [],
  [FilterKeys.Subject]: [],
};

export const TimetableGridFiltersPopup: FC<TimetableGridFiltersPopupProps> = ({
  onClose,
  onSetFilters,
  filters: actualFilters,
  availableAgeGroupIds,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId } = useSchool();
  const [filters, setFilters] = useState<TimetableGridFilters>(actualFilters);

  const handleApply = useCallback(() => {
    onSetFilters(filters);
    onClose();
  }, [onSetFilters, filters, onClose]);

  const handleClear = useCallback(() => {
    setFilters(emptyFilters);
  }, []);

  const { onSelectAgeGroup, onClearAgeGroup, onSelectSubjectId, onClearSubject } = useMemo(() => {
    const clearFilter = (key: keyof TimetableGridFilters) => () => {
      setFilters((prev) => ({ ...prev, [key]: [] }));
    };

    return {
      onSelectAgeGroup: (v: string[]) => {
        setFilters((prev) => ({
          ...prev,
          [FilterKeys.AgeGroup]: toggleMultipleValueArrayProperty(prev[FilterKeys.AgeGroup], v),
        }));
      },
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onSelectSubjectId: (id: string) => {
        setFilters((prev) => ({
          ...prev,
          [FilterKeys.Subject]: prev[FilterKeys.Subject]?.includes(id)
            ? prev[FilterKeys.Subject]?.filter((subjectId) => subjectId !== id)
            : [...(prev[FilterKeys.Subject] || []), id],
        }));
      },
      onClearSubject: clearFilter(FilterKeys.Subject),
    };
  }, []);

  if (!schoolId) return null;

  return (
    <Dialog
      fullWidth
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,

        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
      open
      onClose={onClose}
    >
      <ModalHeader active title={formatMessage({ id: 'timetable-GroupFilter-Title' })}>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        <FilterRow label={formatMessage({ id: 'schoolProperty-AgeGroup' })}>
          <AgeGroupSelectMultiple
            testId={DROPDOWN.AGE_GROUP}
            borderOnHover
            allowArchived
            schoolId={schoolId}
            onClear={onClearAgeGroup}
            optionsTitle={formatMessage({ id: 'schoolProperty-AgeGroup-plural' })}
            userRole={SchoolUserRole.Student}
            availableIds={availableAgeGroupIds}
            selectedIds={filters.age_group || []}
            onSelect={onSelectAgeGroup}
          />
        </FilterRow>
        <FilterRow label={formatMessage({ id: 'groups-GroupSubject' })}>
          <SubjectSelectMultiple
            testId={DROPDOWN.SUBJECT}
            borderOnHover
            schoolId={schoolId}
            onClear={onClearSubject}
            selectedIds={filters.subject || []}
            allowArchived
            onSelectSubjectId={onSelectSubjectId}
          />
        </FilterRow>
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={handleClear}>
          {formatMessage({ id: 'filter-Clear' })}
        </Button>
        <Button onClick={handleApply}>{formatMessage({ id: 'filter-Apply' })}</Button>
      </ModalFooter>
    </Dialog>
  );
};
